import { RowSelectionState } from "@tanstack/react-table";
import { useState, useCallback } from "react";
import { Card } from "@/core/components/ui/card";
import { toast } from "@/core/components/ui/toast";
import { getErrorMessage } from "@/core/api/errors";
import { useBusiness } from "@/core/business";
import { cn } from "@/core/utils/utils";
import { SpiffPaymentsTable } from "@/spiffs/common/spiff-payments-table";
import { SPIFF_SIDEBAR_SECTIONS } from "@/spiffs/constants";
import { useSpiffSidebar } from "@/spiffs/context/spiff-sidebar-context";
import { useBulkDismissSpiffs, useReinstateSpiff } from "@/spiffs/query";
import { EnrichedGroupedPeriodSpiffs } from "@/spiffs/spiffs.d";
import {
  generatePayrollPeriodExcel,
  generatePayrollPeriodExcelFilename,
  downloadExcel,
} from "@/spiffs/utils/payroll-export";
import { EnrichedUserWithSpiffs } from "@/user/user";
import { PayrollPeriodHeader } from "./payroll-period-header";
import { PayrollPeriodTable } from "./payroll-period-table";

interface PayrollPeriodCardProps {
  groupedPeriod: EnrichedGroupedPeriodSpiffs;
  onLockPeriod: (periodId: string) => void;
  canLock: boolean;
  isFlatView: boolean;
  onToggleView: (isFlatView: boolean) => void;
  isExpanded: boolean;
  onToggleExpanded: () => void;
}

export const PayrollPeriodCard = ({
  groupedPeriod,
  onLockPeriod,
  canLock,
  isFlatView,
  onToggleView,
  isExpanded,
  onToggleExpanded,
}: PayrollPeriodCardProps) => {
  const { businessId } = useBusiness();
  const { updateSidebar, state } = useSpiffSidebar();
  const {
    period,
    spiffs,
    userCount,
    totalAmount,
    dismissedCount,
    isLocked,
    enrichedUsers,
  } = groupedPeriod;

  const [selectedSpiffs, setSelectedSpiffs] = useState<RowSelectionState>({});

  const { mutate: dismissSpiffs } = useBulkDismissSpiffs(businessId);
  const { mutateAsync: reinstateSpiff } = useReinstateSpiff(businessId);

  const handleDismissSelected = () => {
    const spiffIds = Object.keys(selectedSpiffs);
    dismissSpiffs(
      { spiffIds },
      {
        onSuccess: () => {
          toast.success("Spiffs dismissed");
          setSelectedSpiffs({});
        },
        onError: (error: unknown) => {
          toast.error("Failed to dismiss spiffs", {
            description: getErrorMessage(error),
          });
        },
      },
    );
  };

  const handleUndismissSpiff = async (spiffId: string) => {
    try {
      await reinstateSpiff({ spiffId });
      toast.success("Spiff reinstated");
    } catch (error) {
      toast.error("Failed to reinstate spiff", {
        description: getErrorMessage(error),
      });
    }
  };

  const handleExport = async () => {
    const workbook = generatePayrollPeriodExcel(enrichedUsers);
    const filename = generatePayrollPeriodExcelFilename(
      period.start,
      period.end,
    );
    await downloadExcel(workbook, filename);
    toast.success("Excel exported");
  };

  const handleGroupedRowClick = useCallback(
    (row: EnrichedUserWithSpiffs) => {
      if (state.selectedItemId === row.id) {
        updateSidebar({
          selectedItemId: null,
          isOpen: false,
          metadata: undefined,
        });
        return;
      }

      updateSidebar({
        isOpen: true,
        section: SPIFF_SIDEBAR_SECTIONS.payrollPeriodUser,
        selectedItemId: row.id,
        metadata: {
          payrollPeriod: groupedPeriod,
        },
      });
    },
    [state.selectedItemId, updateSidebar, groupedPeriod],
  );

  const isEmpty = spiffs.length === 0;

  if (isEmpty) {
    return null;
  }

  return (
    <Card
      className={cn(
        "duration-[1500ms] overflow-hidden transition-[width,max-width] ease-in-out",
        isExpanded
          ? "w-full max-w-full md:max-w-5xl lg:max-w-6xl xl:max-w-7xl"
          : "w-full max-w-full md:max-w-2xl lg:max-w-3xl xl:max-w-4xl",
        isLocked && "opacity-75 transition-opacity duration-500",
      )}
    >
      <PayrollPeriodHeader
        spiffCount={spiffs.filter((s) => !s.dismissed_at).length}
        userCount={userCount}
        totalAmount={totalAmount}
        dismissedCount={dismissedCount}
        isLocked={isLocked}
        selectedCount={Object.keys(selectedSpiffs).length}
        isFlatView={isFlatView}
        onToggleView={onToggleView}
        onDismissSelected={handleDismissSelected}
        onExport={handleExport}
        onLockPeriod={() => onLockPeriod(period.id)}
        canLock={canLock}
        periodStatus={period.status}
        isExpanded={isExpanded}
        onToggleExpanded={onToggleExpanded}
      />

      {isExpanded && (
        <div className="duration-[1500ms] animate-in fade-in slide-in-from-top-2">
          {isFlatView ? (
            <PayrollPeriodTable
              enrichedUsers={enrichedUsers}
              selectedSpiffs={selectedSpiffs}
              setSelectedSpiffs={setSelectedSpiffs}
              isLocked={isLocked}
              onUndismissSpiff={handleUndismissSpiff}
            />
          ) : (
            <SpiffPaymentsTable
              users={enrichedUsers || []}
              selectedUsers={{}}
              setSelectedUsers={() => {}}
              isLoading={false}
              sidebarSection={SPIFF_SIDEBAR_SECTIONS.payrollPeriodUser}
              onRowClick={handleGroupedRowClick}
              hideSelectionColumn={true}
            />
          )}
        </div>
      )}
    </Card>
  );
};
